import React, { useState } from 'react';
import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import { blue, blueGrey } from '@mui/material/colors';
import {
  OtherExpenseCategory,
  OtherExpenseCategoryOption,
  OtherExpensesMargin,
  otherExpenseCategoryKey,
} from 'api/otherExpensesApi';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import MoreVertIcon from '@mui/icons-material/MoreVert';

interface OtherExpensesMarginTableComponentProps {
  otherExpensesMargins: OtherExpensesMargin[];
  // One row each, in display order. Archived custom categories are only
  // passed in when the user asked to see them, and carry an "Archived" badge.
  categories: OtherExpenseCategoryOption[];
  onCategoryClick?: (category: OtherExpenseCategoryOption) => void;
  onRenameCategory?: (category: OtherExpenseCategory) => void;
  onToggleArchiveCategory?: (category: OtherExpenseCategory) => void;
}

export const OtherExpensesMarginTableComponent: React.FC<
  OtherExpensesMarginTableComponentProps
> = ({
  otherExpensesMargins,
  categories,
  onCategoryClick,
  onRenameCategory,
  onToggleArchiveCategory,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<{
    element: HTMLElement;
    category: OtherExpenseCategory;
  } | null>(null);

  // Get unique branch/client combinations
  const getBranchKey = (rate: OtherExpensesMargin): string => {
    if (rate.branchId && rate.clientId) {
      return `${rate.branch?.name || 'Unknown Branch'}/${
        rate.client?.legalName || 'Unknown Client'
      }`;
    } else if (rate.clientId) {
      return rate.client?.legalName || 'Unknown Client';
    } else if (rate.branchId) {
      return rate.branch?.name || 'Unknown Branch';
    } else if (rate.isDefault) {
      return 'Default';
    }
    return 'Unknown';
  };

  const allBranches = Array.from(
    new Set(otherExpensesMargins?.map(getBranchKey) || [])
  )
    .filter(Boolean)
    .sort((a, b) => {
      if (a === 'Default') return -1;
      if (b === 'Default') return 1;
      return a.localeCompare(b);
    });

  // Get latest rate for a category and branch/client combination
  const getLatestRateForCategoryAndBranch = (
    categoryKey: string,
    branchKey: string
  ): number | null => {
    const matchingRates = otherExpensesMargins.filter(
      (rate) =>
        otherExpenseCategoryKey(
          rate.otherExpenseType,
          rate.otherExpenseCategoryId
        ) === categoryKey && getBranchKey(rate) === branchKey
    );

    if (matchingRates.length === 0) return null;

    const latestRate = matchingRates.reduce((latest, current) => {
      return new Date(current.effectiveFrom) > new Date(latest.effectiveFrom)
        ? current
        : latest;
    }, matchingRates[0]);

    return Number(latestRate.margin);
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 5,
                background: blueGrey[50],
                minWidth: 150,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              rowSpan={2}>
              Category
            </TableCell>

            <TableCell
              sx={{
                background: blue[100],
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              align="center"
              colSpan={allBranches.length || 1}>
              Margin
            </TableCell>
          </TableRow>
          <TableRow>
            {allBranches.length > 0 ? (
              allBranches.map((b) => (
                <TableCell
                  sx={{
                    background: blue[50],
                    minWidth: 150,
                    zIndex: 4,
                    position: 'sticky',
                    top: 57,
                    borderRight: '1px solid #ccc',
                  }}
                  key={b}
                  align="center">
                  {b.split('/').map((part, index) => (
                    <Chip
                      key={`${b}-${index}`}
                      label={part}
                      sx={{
                        margin: '2px',
                        color: '#000',
                        backgroundColor: blue[200],
                        paddingInline: '0.5rem',
                        paddingY: '1rem',
                      }}
                      size="small"
                      icon={
                        index === 0 ? (
                          <BusinessIcon
                            color="action"
                            sx={{ fontSize: '1rem', color: '#000' }}
                          />
                        ) : (
                          <AccountBoxIcon
                            color="action"
                            sx={{ fontSize: '1rem', color: '#000' }}
                          />
                        )
                      }
                    />
                  ))}
                </TableCell>
              ))
            ) : (
              <TableCell
                sx={{
                  background: blue[50],
                  zIndex: 4,
                  position: 'sticky',
                  top: 57,
                  borderRight: '1px solid #ccc',
                }}
                align="center">
                <Typography variant="caption">No Margins Added Yet</Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.map((categoryOption) => {
            const customCategory = categoryOption.category;
            const isArchived = Boolean(customCategory?.isArchived);

            return (
              <TableRow key={categoryOption.key} hover>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    background: '#fff',
                    minWidth: 150,
                    borderRight: '1px solid #ccc',
                    fontWeight: 'bold',
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}>
                    <span>
                      {categoryOption.label}
                      {isArchived && (
                        <Chip
                          label="Archived"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </span>
                    {customCategory && (
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${categoryOption.label}`}
                        onClick={(e) =>
                          setMenuAnchor({
                            element: e.currentTarget,
                            category: customCategory,
                          })
                        }>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </TableCell>

                {allBranches.length > 0 ? (
                  allBranches.map((b) => {
                    const marginValue = getLatestRateForCategoryAndBranch(
                      categoryOption.key,
                      b
                    );
                    return (
                      <TableCell
                        key={b}
                        align="center"
                        sx={{
                          borderRight: '1px solid #ccc',
                          cursor: marginValue !== null ? 'pointer' : 'default',
                        }}
                        onClick={() =>
                          marginValue !== null &&
                          onCategoryClick?.(categoryOption)
                        }>
                        {marginValue !== null
                          ? `${marginValue.toFixed(2)}%`
                          : '-'}
                      </TableCell>
                    );
                  })
                ) : (
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ccc' }}>
                    -
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            if (menuAnchor) onRenameCategory?.(menuAnchor.category);
            setMenuAnchor(null);
          }}>
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) onToggleArchiveCategory?.(menuAnchor.category);
            setMenuAnchor(null);
          }}>
          {menuAnchor?.category.isArchived ? 'Restore' : 'Archive'}
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};
